import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const CurrencyCtx = createContext();

const SYMBOLS = {
  USD: "$", GBP: "£", EUR: "€", AED: "AED ", INR: "₹", SAR: "SAR ",
  SGD: "S$", AUD: "A$", CAD: "C$", CNY: "¥", JPY: "¥", HKD: "HK$",
  CHF: "CHF ", NZD: "NZ$", ZAR: "R",
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => localStorage.getItem("wua_currency") || "USD");
  const [rates, setRates] = useState({ USD: 1 });
  const [supported, setSupported] = useState(["USD", "GBP", "EUR", "AED", "INR"]);

  const fetchRates = useCallback(async () => {
    try {
      const { data } = await api.get("/currency/rates");
      setRates(data.rates);
      setSupported(data.supported);
    } catch {}
  }, []);

  useEffect(() => { fetchRates(); }, [fetchRates]);
  useEffect(() => { localStorage.setItem("wua_currency", currency); }, [currency]);

  const convert = (usd) => {
    const r = rates[currency] || 1;
    return (usd || 0) * r;
  };

  // Convert from any source currency (assumes rates are relative to USD).
  const convertFrom = (amount, fromCurrency) => {
    if (!amount) return 0;
    if (!fromCurrency || fromCurrency === "USD") return convert(amount);
    const fromRate = rates[fromCurrency] || 1;
    const usdEquivalent = amount / fromRate;
    return convert(usdEquivalent);
  };

  const format = (usd, opts = {}) => {
    const val = convert(usd);
    const sym = SYMBOLS[currency] || `${currency} `;
    const decimals = opts.decimals ?? (val >= 1000 ? 0 : 2);
    const s = val.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    return `${sym}${s}`;
  };

  // Format `amount` denominated in `fromCurrency` into the user's display currency.
  const formatFrom = (amount, fromCurrency, opts = {}) => {
    const val = convertFrom(amount, fromCurrency);
    const sym = SYMBOLS[currency] || `${currency} `;
    const decimals = opts.decimals ?? (val >= 1000 ? 0 : 2);
    const s = val.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    return `${sym}${s}`;
  };

  return (
    <CurrencyCtx.Provider value={{ currency, setCurrency, rates, supported, convert, convertFrom, format, formatFrom }}>
      {children}
    </CurrencyCtx.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyCtx);
